import api from '@/services/api';

export interface SoundConfig {
  name: 'message' | 'mention' | 'join' | 'leave' | 'mute' | 'unmute' | 'notification' | 'ding';
  frequency: number;
  duration: number;
  description: string;
}

class SoundConfigService {
  private sounds: Map<string, SoundConfig> = new Map();
  private initialized = false;

  /**
   * Initialize - fetch sounds from server
   */
  async initialize(): Promise<void> {
    if (this.initialized) return;

    try {
      const response = await api.get('/sounds');
      const soundsList: SoundConfig[] = response.data.sounds || [];

      // Build map for quick access
      for (const sound of soundsList) {
        this.sounds.set(sound.name, sound);
      }

      this.initialized = true;
    } catch (error) {
      console.warn('Failed to fetch sounds from server, using defaults:', error);
      this.initializeDefaults();
      this.initialized = true;
    }
  }

  /**
   * Get sound configuration by name
   */
  getSound(name: string): SoundConfig | undefined {
    return this.sounds.get(name);
  }

  /**
   * Get all sounds
   */
  getAllSounds(): SoundConfig[] {
    return Array.from(this.sounds.values());
  }

  /**
   * Initialize with default values (fallback)
   */
  private initializeDefaults() {
    const defaults: SoundConfig[] = [
      {
        name: 'message',
        frequency: 800,
        duration: 0.15,
        description: 'Regular message notification',
      },
      {
        name: 'mention',
        frequency: 1200,
        duration: 0.2,
        description: 'User mentioned notification',
      },
      { name: 'join', frequency: 600, duration: 0.3, description: 'User joined voice channel' },
      { name: 'leave', frequency: 400, duration: 0.3, description: 'User left voice channel' },
      { name: 'mute', frequency: 300, duration: 0.2, description: 'Audio muted' },
      { name: 'unmute', frequency: 900, duration: 0.2, description: 'Audio unmuted' },
      {
        name: 'notification',
        frequency: 1000,
        duration: 0.25,
        description: 'General notification',
      },
      { name: 'ding', frequency: 1500, duration: 0.15, description: 'UI interaction ding' },
    ];

    for (const sound of defaults) {
      this.sounds.set(sound.name, sound);
    }
  }
}

export default new SoundConfigService();
